import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw, CheckCircle, MapPin, Mail, User, Briefcase, FileText, FileSignature, CalendarClock, Ban, Undo2 } from 'lucide-react';
import ResetStepDialog from '@/components/organisms/onboarding-management/ResetStepDialog';
import RefuseCandidateDialog from '@/components/organisms/onboarding-management/RefuseCandidateDialog';
import { Label } from '@/components/atoms/texts/label';
import { Button } from '@/components/molecules/buttons/button';
import AvatarOnboarding from '@/components/molecules/onboarding-management/AvatarOnboarding';
import ProgressCandidate from '@/components/molecules/onboarding-management/ProgressCandidate';
import OnboardingStepTag from '@/components/molecules/onboarding-management/OnboardingStepTag';
import OnboardingRefusedTag from '@/components/molecules/onboarding-management/OnboardingRefusedTag';
import ChatSection from '@/components/organisms/onboarding-management/chat/ChatSection';
import useOnboardingCandidate from '@/services/admin-services/query/useOnboardingCandidate';
import useOnboardingResetCandidateStep from '@/services/admin-services/mutation/useOnboardingResetCandidateStep';
import useOnboardingRefuseCandidate from '@/services/admin-services/mutation/useOnboardingRefuseCandidate';
import useOnboardingLiftCandidateRefusal from '@/services/admin-services/mutation/useOnboardingLiftCandidateRefusal';
import useOnboardingSubmitSynapseValidationStep from '@/services/admin-services/mutation/useOnboardingSubmitSynapseValidationStep';
import useGenerateCandidateContracts from '@/services/admin-services/mutation/useGenerateCandidateContracts';
import useOnboardingSubmitContractGenerationStep from '@/services/admin-services/mutation/useOnboardingSubmitContractGenerationStep';
import { useToast } from '@/components/organisms/feedback/use-toast';
import { formatDate } from '@/utils/formatDate';
import { getCountryList } from '@/utils/getCountriesLanguagesAndCode';
import { formatPhoneNumberWithCountryCode, getPhoneNumberHref } from '@/utils/formatPhoneNumber';
import useOnboardingUploadedDocuments from '@/services/admin-services/query/useOnboardingUploadedDocuments';
import useOnboardingAppointmentPreferences from '@/services/admin-services/query/useOnboardingAppointmentPreferences';
import useGetOnboardingDocument from '@/services/admin-services/api-rest/useGetOnboardingDocument';
import Document from '@/components/organisms/onboarding-management/Document';
import PrintErrorMessage from '../feedback/PrintErrorMessage';
import PrintWarningMessage from '@/components/organisms/feedback/PrintWarningMessagee';

const COUNTRY_LIST = getCountryList();
const DOCUMENT_TYPES = ['CV', 'ID_CARD_FRONT', 'ID_CARD_BACK', 'BANK_ACCOUNT_DETAILS', 'CRIMINAL_RECORD'];
const CHAT_DISPLAY_MODE_STORAGE_KEY = 'onboarding-chat-display-mode';

const CandidateDetails = ({ candidateUuid }) => {
    const [t] = useTranslation('global');
    const { toast } = useToast();
    const { data: candidate, loading, error, refetch } = useOnboardingCandidate(candidateUuid);
    const { data: uploadedDocuments, loading: documentsLoading, error: documentsError, refetch: refetchDocuments } = useOnboardingUploadedDocuments(candidateUuid);
    const { data: appointmentPreferences, refetch: refetchAppointmentPreferences } = useOnboardingAppointmentPreferences(candidateUuid);
    const { resetCandidateStep, loading: resetLoading, error: resetError } = useOnboardingResetCandidateStep();
    const { submitSynapseValidationStep, loading: validateLoading } = useOnboardingSubmitSynapseValidationStep();
    const { generateCandidateContracts, loading: generateContractsLoading } = useGenerateCandidateContracts();
    const { submitContractGenerationStep, loading: submitContractLoading } = useOnboardingSubmitContractGenerationStep();
    const { refuseCandidate, loading: refuseLoading, error: refuseError } = useOnboardingRefuseCandidate();
    const { liftCandidateRefusal, loading: liftRefusalLoading } = useOnboardingLiftCandidateRefusal();
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [openRefuseDialog, setOpenRefuseDialog] = useState(false);
    const [isChatAnchored, setIsChatAnchored] = useState(() => window.localStorage.getItem(CHAT_DISPLAY_MODE_STORAGE_KEY) === 'anchored');
    const [candidateView, setCandidateView] = useState(null);
    const [uploadedDocumentsView, setUploadedDocumentsView] = useState(null);
    const candidateSignatureRef = useRef(null);
    const uploadedDocumentsSignatureRef = useRef(null);
    const { getDocument: getOnboardingDocument, loading: onboardingDocumentLoading, error: onboardingDocumentError } = useGetOnboardingDocument();

    useEffect(() => {
        setCandidateView(null);
        setUploadedDocumentsView(null);
        candidateSignatureRef.current = null;
        uploadedDocumentsSignatureRef.current = null;
    }, [candidateUuid]);

    useEffect(() => {
        if (!candidate) return;

        const nextSignature = JSON.stringify(candidate);
        if (candidateSignatureRef.current === nextSignature) {
            return;
        }

        candidateSignatureRef.current = nextSignature;
        setCandidateView(candidate);
    }, [candidate]);

    useEffect(() => {
        if (!uploadedDocuments) return;

        const nextSignature = JSON.stringify(uploadedDocuments);
        if (uploadedDocumentsSignatureRef.current === nextSignature) {
            return;
        }

        uploadedDocumentsSignatureRef.current = nextSignature;
        setUploadedDocumentsView(uploadedDocuments);
    }, [uploadedDocuments]);

    // Polling silencieux: refresh toutes les 15s si l'onglet est actif
    useEffect(() => {
        const POLLING_INTERVAL_MS = 15000; // 15 secondes

        const silentRefetch = async () => {
            if (document.hidden) {
                return;
            }
            try {
                await refetch();
                await refetchDocuments();
                await refetchAppointmentPreferences();
            } catch (err) {
                // Erreur silencieuse en polling
                console.debug('Silent polling error:', err);
            }
        };

        // Listener pour refetch au retour de l'onglet
        const handleVisibilityChange = async () => {
            if (!document.hidden) {
                await silentRefetch();
            }
        };

        // Configuration du polling
        const pollingInterval = setInterval(silentRefetch, POLLING_INTERVAL_MS);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(pollingInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [candidateUuid, refetch, refetchDocuments, refetchAppointmentPreferences]);


    const handleRefresh = async () => {
        try {
            await refetch();
            await refetchDocuments();
            await refetchAppointmentPreferences();
            toast({
                title: t('management.onboarding.feedback.refreshSuccessTitle'),
                description: t('management.onboarding.feedback.refreshSuccessDescription'),
                variant: 'success',
            });
        } catch (err) {
            toast({
                title: t('management.onboarding.feedback.refreshErrorTitle'),
                description: err.message,
                variant: 'error',
            });
        }
    };

    const handleReset = async (step, comment) => {
        try {
            await resetCandidateStep(candidateUuid, step, comment);
            await refetch();
            setOpenResetDialog(false);
            toast({
                title: t('management.onboarding.feedback.resetStepSuccessTitle'),
                description: t('management.onboarding.feedback.resetStepSuccessDescription'),
                variant: 'success',
            });
        } catch (err) {
            toast({
                title: t('management.onboarding.feedback.resetStepErrorTitle'),
                description: err.message,
                variant: 'error',
            });
        }
    };

    // Applique immediatement l'etat de refus renvoye par la mutation, sans attendre le refetch du candidat.
    const applyRefusalState = (refusalState) => {
        if (!refusalState) return;
        setCandidateView((previous) => (previous ? { ...previous, ...refusalState } : previous));
    };

    const refetchCandidateSilently = () => {
        refetch().catch((err) => console.debug('Candidate refetch error:', err));
    };

    const handleRefuse = async (refusalComment) => {
        try {
            const result = await refuseCandidate(candidateUuid, refusalComment);
            applyRefusalState(result?.data?.onboardingRefuseCandidate);
            setOpenRefuseDialog(false);
            toast({
                title: t('management.onboarding.feedback.refuseCandidateSuccessTitle'),
                description: t('management.onboarding.feedback.refuseCandidateSuccessDescription'),
                variant: 'success',
            });
        } catch (err) {
            toast({
                title: t('management.onboarding.feedback.refuseCandidateErrorTitle'),
                description: err.message,
                variant: 'error',
            });
            return;
        }
        refetchCandidateSilently();
    };

    const handleLiftRefusal = async () => {
        try {
            const result = await liftCandidateRefusal(candidateUuid);
            applyRefusalState(result?.data?.onboardingLiftCandidateRefusal);
            toast({
                title: t('management.onboarding.feedback.liftRefusalSuccessTitle'),
                description: t('management.onboarding.feedback.liftRefusalSuccessDescription'),
                variant: 'success',
            });
        } catch (err) {
            toast({
                title: t('management.onboarding.feedback.liftRefusalErrorTitle'),
                description: err.message,
                variant: 'error',
            });
            return;
        }
        refetchCandidateSilently();
    };

    const handleValidateSynapse = async () => {
        try {
            await submitSynapseValidationStep(candidateUuid);
            await refetch();
            toast({
                title: t('management.onboarding.feedback.validateSynapseSuccessTitle'),
                description: t('management.onboarding.feedback.validateSynapseSuccessDescription'),
                variant: 'success',
            });
        } catch (err) {
            toast({
                title: t('management.onboarding.feedback.validateSynapseErrorTitle'),
                description: err.message,
                variant: 'error',
            });
        }
    };

    const handleGenerateContracts = async () => {
        try {
            await generateCandidateContracts(candidateUuid);
            await refetch();
            toast({
                title: t('management.onboarding.feedback.generateContractsSuccessTitle'),
                description: t('management.onboarding.feedback.generateContractsSuccessDescription'),
                variant: 'success',
            });
        } catch (err) {
            toast({
                title: t('management.onboarding.feedback.generateContractsErrorTitle'),
                description: err.message,
                variant: 'error',
            });
        }
    };

    const handleValidateContractGeneration = async () => {
        try {
            await submitContractGenerationStep(candidateUuid);
            await refetch();
            toast({
                title: t('management.onboarding.feedback.validateContractGenerationSuccessTitle'),
                description: t('management.onboarding.feedback.validateContractGenerationSuccessDescription'),
                variant: 'success',
            });
        } catch (err) {
            toast({
                title: t('management.onboarding.feedback.validateContractGenerationErrorTitle'),
                description: err.message,
                variant: 'error',
            });
        }
    };

    if (loading && !candidateView) {
        return <div className="h-full w-full rounded-md border bg-white flex items-center justify-center"><span className="loader" /></div>;
    }

    if (error && !candidateView) {
        return <div className="h-full w-full rounded-md border bg-white flex items-center justify-center"><Label message="error.fetchUnsuccessfull" /></div>;
    }

    if (!candidateView) {
        return <div className="h-full w-full rounded-md border bg-white" />;
    }

    const STEP_FLOW = ['PERSONAL_INFO', 'DOCUMENTS', 'CURRENT_MISSION', 'SYNAPSE_VALIDATION', 'ADMINISTRATIVE_INFO', 'CONTRACT_GENERATION', 'OFFICE_APPOINTMENT', 'COMPLETE'];
    const stepIndex = STEP_FLOW.indexOf(candidateView?.step);
    const currentStepIndex = stepIndex >= 0 ? stepIndex : 0;
    const totalSteps = STEP_FLOW.length;
    const isSynapseValidationStep = candidateView?.step === 'SYNAPSE_VALIDATION';
    const isContractGenerationStep = candidateView?.step === 'CONTRACT_GENERATION';
    // PERSONAL_INFO est la premiere etape du workflow : aucun retour en arriere possible.
    const isFirstOnboardingStep = candidateView?.step === 'PERSONAL_INFO';
    const isRefused = Boolean(candidateView?.refused);
    const actionsLoading = resetLoading || validateLoading || generateContractsLoading || submitContractLoading || refuseLoading || liftRefusalLoading;
    const normalizedCountryCode = String(candidateView?.countryCode || '').toUpperCase();
    const countryName = normalizedCountryCode ? (COUNTRY_LIST[normalizedCountryCode]?.name || normalizedCountryCode) : '-';
    const formattedPhoneNumber = formatPhoneNumberWithCountryCode(candidateView?.phoneNumber, normalizedCountryCode);
    const phoneNumberHref = getPhoneNumberHref(candidateView?.phoneNumber, normalizedCountryCode);
    const candidateName = `${candidateView?.firstname || ''} ${candidateView?.lastname || ''}`.trim();
    const uploadedDocumentTypes = new Set((uploadedDocumentsView || []).map((document) => document.documentType));
    const uploadedCount = DOCUMENT_TYPES.filter((type) => uploadedDocumentTypes.has(type)).length;
    const missingCount = DOCUMENT_TYPES.length - uploadedCount;
    const hasUploadedDocumentsView = uploadedDocumentsView !== null;

    const sections = [
        {
            icon: <User className="h-4 w-4 text-greyStrong" />,
            title: t('management.onboarding.overview.personalInfo'),
            fields: [
                { label: t('management.onboarding.fields.gender'), value: candidateView.gender ? t(`management.onboarding.gender.${candidateView.gender}`) : '-' },
                { label: t('management.onboarding.fields.birthdate'), value: candidateView.birthdate ? formatDate(new Date(candidateView.birthdate)) : '-' },
                { label: t('management.onboarding.fields.language'), value: candidateView.language || '-' },
                { label: t('management.onboarding.fields.cooptant'), value: candidateView.cooptant || '-' },
                { label: t('management.onboarding.fields.socialSecurityNumber'), value: candidateView.socialSecurityNumber || '-' },
            ],
        },
        {
            icon: <Mail className="h-4 w-4 text-greyStrong" />,
            title: t('management.onboarding.overview.contact'),
            fields: [
                { label: t('management.onboarding.fields.email'), value: candidateView.email, link: true, href: `mailto:${candidateView.email}` },
                { label: t('management.onboarding.fields.phoneNumber'), value: formattedPhoneNumber || '-', link: Boolean(phoneNumberHref), href: phoneNumberHref ? `tel:${phoneNumberHref}` : undefined },
            ],
        },
        {
            icon: <MapPin className="h-4 w-4 text-greyStrong" />,
            title: t('management.onboarding.overview.address'),
            fields: [
                { label: t('management.onboarding.fields.addressLine1'), value: candidateView.addressLine1 || '-' },
                { label: t('management.onboarding.fields.addressLine2'), value: candidateView.addressLine2 || '-' },
                { label: t('management.onboarding.fields.cityAndZipCode'), value: `${candidateView.city || '-'} · ${candidateView.zipCode || '-'}` },
                { label: t('management.onboarding.fields.country'), value: countryName },
            ],
        },
    ];

    if (candidateView.onboardingCurrentMission) {
        const currentMission = candidateView.onboardingCurrentMission;
        const missionContactPhoneNumber = formatPhoneNumberWithCountryCode(currentMission.contactPhoneNumber, normalizedCountryCode);
        const missionContactPhoneHref = getPhoneNumberHref(currentMission.contactPhoneNumber, normalizedCountryCode);

        sections.push({
            icon: <Briefcase className="h-4 w-4 text-greyStrong" />,
            title: t('management.onboarding.overview.currentMission'),
            rowSpan: true,
            fields: [
                { label: t('management.onboarding.fields.jobTitle'), value: currentMission.jobTitle || '-' },
                { label: t('management.onboarding.fields.client'), value: currentMission.client || '-' },
                { label: t('management.onboarding.fields.actualDailyRate'), value: currentMission.actualDailyRate ? t('management.onboarding.mission.dailyRateValue', { amount: currentMission.actualDailyRate }) : '-' },
                { label: t('management.onboarding.fields.workingDaysPerYear'), value: currentMission.workingDaysPerYear ? t('management.onboarding.mission.workingDaysValue', { count: currentMission.workingDaysPerYear }) : '-' },
                { label: t('management.onboarding.fields.desiredStartDate'), value: currentMission.desiredStartDate ? formatDate(new Date(currentMission.desiredStartDate)) : '-' },
                { label: t('management.onboarding.fields.contactName'), value: currentMission.contactName || '-' },
                { label: t('management.onboarding.fields.contactEmail'), value: currentMission.contactEmail || '-', link: Boolean(currentMission.contactEmail), href: currentMission.contactEmail ? `mailto:${currentMission.contactEmail}` : undefined },
                { label: t('management.onboarding.fields.contactPhoneNumber'), value: missionContactPhoneNumber || '-', link: Boolean(missionContactPhoneHref), href: missionContactPhoneHref ? `tel:${missionContactPhoneHref}` : undefined },
                { label: t('management.onboarding.fields.missionDescription'), value: currentMission.missionDescription || '-', multiline: true },
            ],
        });
    }

    sections.push({
        icon: <CalendarClock className="h-4 w-4 text-greyStrong" />,
        title: t('management.onboarding.appointments.title'),
        fields: appointmentPreferences.length > 0
            ? appointmentPreferences.map((preference) => ({
                label: t(`management.onboarding.day.${preference.preferredDay}`),
                value: t(`management.onboarding.timeSlot.${preference.timeSlot}`),
            }))
            : [{ label: t('management.onboarding.empty.noAppointments'), value: '-' }],
    });

    const handleOpenOnboardingDocument = async (documentType) => {
        try {
            await getOnboardingDocument(candidateUuid, documentType);
        } catch (downloadError) {
            toast({
                title: t('error.fetchUnsuccessfull'),
                description: downloadError?.message || t('error.fetchUnsuccessfull'),
                variant: 'error',
            });
        }
    };

    return (
        <div className="h-full w-full overflow-y-auto ">
            <div className=" p-secondDegree flex flex-col gap-secondDegree">
                {/* Header */}
                <div className="flex items-start justify-between gap-firstDegree pb-firstDegree border-b ">
                    <div className="flex items-start gap-firstDegree">
                        <AvatarOnboarding firstName={candidateView.firstname} lastName={candidateView.lastname} step={candidateView.step} className="h-12 w-12" />
                        <div className="flex flex-col gap-1 ">
                            <Label fontWeight="bold" className="text-lg">{candidateView.firstname} {candidateView.lastname}</Label>

                            <div className="flex items-center gap-2 flex-wrap transition-all duration-1000 ease-out motion-reduce:transition-none">
                                <OnboardingStepTag step={candidateView.step} />
                                {isRefused && <OnboardingRefusedTag refusedAt={candidateView.refusedAt} />}
                                {/* Progress Bar */}
                                <ProgressCandidate step={candidateView.step} className="grow" />
                                {/* <Label className="text-sm text-greyStrong">Étape {currentStepIndex + 1} / {totalSteps} ·</Label> */}
                                {/* {candidate.step === 'CONTRACT_GENERATION' && (
                                    <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">Signature contrat</span>
                                )} */}
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col lg:flex-row items-center gap-1 justify-end transition-all duration-1000 ease-out motion-reduce:transition-none">

                        <Button
                            style="ghost"
                            showLeftIcon
                            leftIcon={<RotateCcw className="h-4 w-4" />}
                            text="management.onboarding.actions.resetStep"
                            onClick={() => setOpenResetDialog(true)}
                            disabled={actionsLoading || isFirstOnboardingStep || isRefused}
                        />
                        {isRefused ? (
                            <Button
                                style="outline"
                                showLeftIcon
                                leftIcon={<Undo2 className="h-4 w-4" />}
                                text="management.onboarding.actions.liftRefusal"
                                onClick={handleLiftRefusal}
                                disabled={actionsLoading}
                            />
                        ) : (
                            <Button
                                style="ghost"
                                showLeftIcon
                                leftIcon={<Ban className="h-4 w-4" />}
                                text="management.onboarding.actions.refuseCandidate"
                                onClick={() => setOpenRefuseDialog(true)}
                                disabled={actionsLoading}
                            />
                        )}
                        {isSynapseValidationStep && !isRefused && (
                            <Button
                                style="fill"
                                showLeftIcon
                                leftIcon={<CheckCircle className="h-4 w-4" />}
                                text="management.onboarding.actions.validateSynapse"
                                onClick={handleValidateSynapse}
                                disabled={actionsLoading}
                            />
                        )}
                        {isContractGenerationStep && !isRefused && (
                            <>
                                <Button
                                    style="outline"
                                    showLeftIcon
                                    leftIcon={<FileSignature className="h-4 w-4" />}
                                    text="management.onboarding.actions.generateContracts"
                                    onClick={handleGenerateContracts}
                                    disabled={actionsLoading}
                                />
                                <Button
                                    style="fill"
                                    showLeftIcon
                                    leftIcon={<CheckCircle className="h-4 w-4" />}
                                    text="management.onboarding.actions.validateContractGeneration"
                                    onClick={handleValidateContractGeneration}
                                    disabled={actionsLoading}
                                />
                            </>
                        )}
                    </div>
                </div>



                {isRefused && (
                    <PrintWarningMessage
                        isComponent
                        warning={(
                            <div className="flex flex-col gap-1">
                                <Label fontWeight="bold">
                                    {candidateView.refusedAt
                                        ? t('management.onboarding.refusal.refusedOn', { date: formatDate(new Date(candidateView.refusedAt)) })
                                        : t('management.onboarding.refusal.refused')}
                                </Label>
                                {candidateView.refusalComment && (
                                    <Label className="whitespace-pre-line">{candidateView.refusalComment}</Label>
                                )}
                            </div>
                        )}
                    />
                )}

                {/* Metadata */}
                <Label className="text-xs text-greyStrong">
                    {t('management.onboarding.overview.createdOn', { date: new Date().toLocaleDateString('fr-FR') })}
                </Label>


                <div className={`grid gap-secondDegree transition-all duration-300 ${isChatAnchored ? 'grid-cols-1 xl:grid-cols-3' : 'grid-cols-1'}`}>
                    {/* Sections Grid */}
                    <div className={`flex flex-col gap-secondDegree ${isChatAnchored ? 'col-span-1 xl:col-span-2' : 'col-span-1'}`}>

                        <div className=" grid grid-cols-2 auto-rows-min items-start gap-secondDegree mt-firstDegree">
                            {sections.map((section, idx) => (
                                <div key={idx} className={`p-secondDegree  rounded-md bg-white ${section.rowSpan ? 'row-span-2' : ''}`}>
                                    <div className="flex items-center gap-2 mb-secondDegree">
                                        <span className="inline-flex items-center justify-center">{section.icon}</span>
                                        <Label fontWeight="bold" className="text-sm">{section.title}</Label>
                                    </div>
                                    <div className="space-y-firstDegree">
                                        {section.fields.map((field, fidx) => (
                                            <div key={fidx} className={field.multiline ? 'flex flex-col gap-1' : 'flex items-center justify-between'}>
                                                <Label fontWeight="bold">{field.label}</Label>
                                                {field.link ? (
                                                    <Label href={field.href} className="hover:underline ">
                                                        {field.value}
                                                    </Label>
                                                ) : (
                                                    <Label className={field.multiline ? 'whitespace-pre-line' : ''}>{field.value}</Label>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className=" rounded-md flex flex-col bg-white p-firstDegree gap-firstDegree">
                            <div className="mb-firstDegree flex items-center justify-between">
                                <span className="inline-flex items-center gap-1 text-night">
                                    <FileText className="h-4 w-4" />
                                    <Label fontWeight="bold" className="uppercase">{t('management.onboarding.tabs.documents')}</Label>
                                </span>

                                <div className="inline-flex items-center gap-1 text-sm">
                                    <Label className="uppercase">{t('management.onboarding.documents.receivedCount', { count: uploadedCount })}</Label>
                                    <span className="text-greyStrong">·</span>
                                    <Label className="uppercase text-[#A32D2D]">{t('management.onboarding.documents.missingCount', { count: missingCount })}</Label>
                                </div>
                            </div>

                            {documentsLoading && !hasUploadedDocumentsView && <span className="loader" />}
                            {!documentsLoading && !hasUploadedDocumentsView && (documentsError || onboardingDocumentError) && <PrintErrorMessage error={documentsError || onboardingDocumentError} />}
                            {/* {!documentsLoading && !documentsError && onboardingDocumentError && <PrintErrorMessage color="error" message="error.fetchUnsuccessfull" />} */}

                            {!documentsError && hasUploadedDocumentsView && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-firstDegree">
                                    {DOCUMENT_TYPES.map((docType) => {
                                        const uploaded = uploadedDocumentTypes.has(docType);

                                        return (
                                            <Document
                                                key={docType}
                                                documentType={docType}
                                                uploaded={uploaded}
                                                loading={onboardingDocumentLoading}
                                                displayWarning={candidateView.step !== 'PERSONAL_INFO'}
                                                onDownload={() => {
                                                    if (uploaded) {
                                                        void handleOpenOnboardingDocument(docType);
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={isChatAnchored ? 'col-span-1 min-h-0' : 'h-0 w-0 overflow-visible'}>
                        <ChatSection
                            candidateUuid={candidateUuid}
                            candidateName={candidateName || '-'}
                            onAnchorStateChange={setIsChatAnchored}
                        />
                    </div>
                </div>
                
            </div>
            <ResetStepDialog
                open={openResetDialog}
                onOpenChange={setOpenResetDialog}
                candidateStep={candidateView?.step}
                onConfirm={handleReset}
                loading={resetLoading}
                error={resetError}
            />
            <RefuseCandidateDialog
                open={openRefuseDialog}
                onOpenChange={setOpenRefuseDialog}
                onConfirm={handleRefuse}
                loading={refuseLoading}
                error={refuseError}
            />
        </div>
    );
};

export default CandidateDetails;
